import React, { useState, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import { useToast } from '@/components/ui/use-toast';
import { sendContactForm } from '@/utils/contactFormService';
import { calculateResults } from '@/utils/calculatorLogic';

// Import refactored components
import CalculatorHero from '@/components/calculator/CalculatorHero';
import TelemarketingInfoSection from '@/components/calculator/TelemarketingInfoSection';
import CalculatorForm, { CalculatorFormValues } from '@/components/calculator/CalculatorForm';
import CalculatorResult from '@/components/calculator/CalculatorResult';
import ExcelDownloadCard from '@/components/calculator/ExcelDownloadCard';
import EmailCaptureDialog, { EmailCaptureFormValues } from '@/components/calculator/EmailCaptureDialog';
import ContactForm from '@/components/calculator/ContactForm';
import { CalculatorContactFormValues } from '@/components/calculator/ContactFormSchema';
import TransparencySection from '@/components/calculator/TransparencySection';
import CTASection from '@/components/calculator/CTASection';
import ToolsOverviewSection from '@/components/calculator/ToolsOverviewSection';

// Function to send calculator results to admin
const sendCalculatorResultsToAdmin = async (values: CalculatorFormValues, result: any, userEmail?: string) => {
  // Create additional info object with all the calculator details
  const calculatorDetails = {
    'Gewenste afspraken per maand': values.afspraken,
    'Doelgroep': values.doelgroep,
    'Regio': values.regio,
    'CRM Integratie': values.crmIntegratie,
    'Contactpersonen': values.contactpersonen,
    'Taal': values.taal,
    'DMU Complexiteit': values.dmuComplexiteit,
    'Product Complexiteit': values.productComplexiteit,
    'Leads Type': values.leadsType,
    'Marktgrootte': values.marktgrootte,
    'Conversiepercentage': `${values.conversiePercentage}%`,
    'Campagneduur': `${values.campagneDuur} maanden`,
    'Offertepercentage': `${values.offertePercentage}%`,
    'Gemiddelde dealwaarde': `€${values.dealValue}`,
    'Closing percentage': `${values.closingPercentage}%`,
  };
  
  // Results object
  const resultDetails = {
    'Maximaal haalbare afspraken per maand': result.maxAfspraken,
    'Prijs per warme afspraak': `€${result.prijs}`,
    'Totale maandkost': `€${result.totaal}`,
    'Verwachte maandomzet': `€${result.verwachteOmzet}`,
    'ROI': `${result.roi}%`,
    'Overschreden': result.overschreden ? 'Ja' : 'Nee',
  };
  
  // Combine calculator inputs and results
  const additionalInfo = {
    ...calculatorDetails,
    ...resultDetails
  };
  
  // Use the global contact form service
  const formData = {
    email: userEmail || 'Anonieme gebruiker',
    bedrijf: values.bedrijf || '',
    naam: values.naam || '',
    telefoonnummer: values.telefoonnummer || '',
  };
  
  const subject = userEmail 
    ? `Nieuwe Excel Calculator Download - ${userEmail}`
    : 'Nieuwe Calculator Resultaten';
  
  // Pass userEmail as the ccEmail parameter
  return await sendContactForm(formData, subject, additionalInfo, userEmail);
};

const Calculator = () => {
  const { toast } = useToast();
  const [result, setResult] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [formValues, setFormValues] = useState<CalculatorFormValues | null>(null);

  const onSubmit = async (values: CalculatorFormValues) => {
    setLoading(true);
    setFormValues(values);
    
    // Calculate results with a small delay to show loading state
    setTimeout(async () => {
      const calculatedResult = calculateResults(values);
      setResult(calculatedResult);
      
      // Send results to admin email when a calculation is complete
      await sendCalculatorResultsToAdmin(values, calculatedResult);
      
      setLoading(false);
      
      // Scroll to result
      setTimeout(() => {
        const resultElement = document.getElementById('result-section');
        if (resultElement) {
          resultElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }, 1000);
  };

  const handleDownloadExcel = () => {
    // Show the email capture dialog
    setShowEmailCapture(true);
  };

  const handleEmailCaptureSubmit = async (data: EmailCaptureFormValues) => {
    // Send email notification with the user's data
    if (result && formValues) {
      await sendCalculatorResultsToAdmin(formValues, result, data.email);
    }
    
    // Close the dialog
    setShowEmailCapture(false);
    
    // Show success toast
    toast({
      title: "Excel template aangevraagd!",
      description: `Je krijgt binnen enkele minuten een e-mail op ${data.email} met de volledige leadcalculator met marktcheck.`,
    });
  };

  const handleContactForm = async (data: CalculatorContactFormValues) => {
    // Send email notification with the contact form data
    if (result && formValues) {
      await sendCalculatorResultsToAdmin({
        ...formValues,
        ...data
      }, result, data.email);
    }
    
    toast({
      title: "Bedankt voor je aanvraag!",
      description: "We nemen binnen 24 uur contact met je op voor een vrijblijvend gesprek.",
    });
    setShowContactForm(false);
  };

  // Handle anchor link
  useEffect(() => {
    if (window.location.hash === '#calculator') {
      const calculatorForm = document.getElementById('calculator-form');
      if (calculatorForm) {
        setTimeout(() => {
          calculatorForm.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <CalculatorHero />

      {/* Telemarketing Scan CTA Banner */}
      <TelemarketingInfoSection />

      {/* Calculator Section */}
      <section className="section bg-brand-gray" id="calculator-form">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Lead Calculator</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Vul de onderstaande gegevens in om jouw persoonlijke prijs per warme afspraak te berekenen.
            </p>
          </div>
          
          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-3 bg-white p-6 md:p-8 rounded-lg shadow-md border border-gray-200">
              <CalculatorForm onSubmit={onSubmit} loading={loading} />
            </div>
            
            <div className="md:col-span-2">
              <ExcelDownloadCard onDownload={handleDownloadExcel} />
              
              {result && formValues && (
                <CalculatorResult 
                  result={result} 
                  afspraken={formValues.afspraken}
                  onRequestQuote={() => setShowContactForm(true)}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Email capture dialog */}
      <EmailCaptureDialog
        open={showEmailCapture}
        onOpenChange={setShowEmailCapture}
        onSubmit={handleEmailCaptureSubmit}
      />

      {/* Contact form */}
      {showContactForm && (
        <ContactForm 
          onSubmit={handleContactForm}
          onCancel={() => setShowContactForm(false)}
        />
      )}

      {/* Transparency Section */}
      <TransparencySection />
      
      {/* Tools Overview Section */}
      <ToolsOverviewSection />
      
      {/* CTA Section */}
      <CTASection onShowContactForm={() => setShowContactForm(true)} />
    </div>
  );
};

export default Calculator;
